<template>
  <MainViewContainer>
    <div class="col-span-12">
      <h1 v-if="page">{{ page.title }}</h1>
    </div>
    <div class="col-span-12">
      <p v-if="error">{{ $t('default.material_bank.error') }}</p>
      <template v-else-if="!loading">
        <div
          v-for="section in sections"
          :key="section.id"
          class="section-grid mb-8"
          :style="{ '--section-columns': section.columns }"
        >
          <div
            v-for="content in sortedContents(section)"
            :key="content.id"
            class="section-content-item"
          >
            <VMarkdownView
              v-if="content.text"
              :content="content.text"
              mode="light"
            />
            <img
              v-if="content.image_url"
              :src="content.image_url"
              class="max-w-full mt-2"
              alt=""
            />
            <div v-if="content.video_url" class="video-embed mt-2">
              <iframe
                :src="embedUrl(content.video_url)"
                frameborder="0"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
                class="w-full"
                style="aspect-ratio: 16/9;"
              ></iframe>
            </div>
          </div>
        </div>
      </template>

      <div v-if="page" class="flex justify-end mt-8">
        <MaterialPageNavigation :navigation="page.navigation" />
      </div>

      <div v-if="page" class="discussions-toggle">
        <button
          type="button"
          class="discussions-toggle__btn"
          :class="{ 'discussions-toggle__btn--active': activeDiscussionPanel === 'public' }"
          @click="activeDiscussionPanel = 'public'"
        >
          {{ $t('default.discussions.toggle_public') }}
        </button>
        <button
          type="button"
          class="discussions-toggle__btn"
          :class="{ 'discussions-toggle__btn--active': activeDiscussionPanel === 'direct' }"
          @click="activeDiscussionPanel = 'direct'"
        >
          {{ $t('default.discussions.toggle_direct') }}
        </button>
      </div>

      <PageDiscussions v-if="page && activeDiscussionPanel === 'public'" :page="page" />
      <PageDirectMessageListing v-else-if="page && activeDiscussionPanel === 'direct'" :page="page" />
    </div>
  </MainViewContainer>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import MainViewContainer from '@/components/MainViewContainer'
import MaterialPageNavigation from '@/components/material-bank/MaterialPageNavigation.vue'
import PageDiscussions from '@/components/discussions/PageDiscussions.vue'
import PageDirectMessageListing from '@/components/discussions/PageDirectMessageListing.vue'
import { VMarkdownView } from 'vue3-markdown'
import 'vue3-markdown/dist/vue3-markdown.css'

const route = useRoute()
const store = useStore()

const activeDiscussionPanel = ref('public')

const page = computed(() => store.state.materialBank.currentPage)
const loading = computed(() => store.state.materialBank.loading)
const error = computed(() => store.state.materialBank.error)

const sections = computed(() => {
  if (!page.value) return []
  return [...(page.value.sections || [])].sort((a, b) => a.order - b.order)
})

function sortedContents(section) {
  return [...(section.contents || [])].sort((a, b) => a.order - b.order)
}

function embedUrl(url) {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
  return url
}

const pagePath = computed(() => {
  const pathMatch = route.params.pathMatch
  return Array.isArray(pathMatch) ? pathMatch.join('/') : pathMatch
})

onMounted(() => {
  store.dispatch('materialBank/fetchPage', pagePath.value)
})

watch(pagePath, (newPath, oldPath) => {
  if (newPath === oldPath) return
  store.dispatch('materialBank/fetchPage', newPath)
})
</script>

<style scoped>
.discussions-toggle {
  display: flex;
  gap: 0.75rem;
  margin: 3rem 0 0;
}

.discussions-toggle__btn {
  background: white;
  color: var(--text-black);
  border: 1px solid var(--gray-200);
}

.discussions-toggle__btn:hover {
  background: var(--gray-100);
}

.discussions-toggle__btn--active,
.discussions-toggle__btn--active:hover {
  background: var(--halsa-green);
  color: white;
  border-color: var(--halsa-green);
}

.section-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .section-grid {
    grid-template-columns: repeat(var(--section-columns, 1), 1fr);
  }
}
</style>
